import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { exportGif } from '../image_utilities/ExportGif';

// Render the same encoded bytes as Save, including palette, timing and disposal.
const GifPreview = ({ images, timeline, width, height, backgroundColor,
  transparentColor, transparencyTolerance, keepPreviousFrames, repeat }) => {
  const options = useMemo(() => ({ images, timeline, width, height, backgroundColor,
    transparentColor, transparencyTolerance, keepPreviousFrames, repeat }),
  [images, timeline, width, height, backgroundColor, transparentColor,
    transparencyTolerance, keepPreviousFrames, repeat]);
  const [result, setResult] = useState(null);
  const [replay, setReplay] = useState(0);
  const current = result?.options === options ? result : null;

  useEffect(() => {
    const controller = new AbortController();
    let url;
    const timer = setTimeout(async () => {
      try {
        const blob = await exportGif({ ...options, signal: controller.signal });
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(blob);
        setResult({ options, url });
      } catch (error) {
        if (!controller.signal.aborted) {
          setResult({ options, error: 'Could not prepare the preview. Change an option or reopen Save Options to retry.' });
        }
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [options]);

  return (
    <Box component="section" aria-label="GIF preview" sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography component="h3" variant="subtitle2">Preview</Typography>
        {current?.url && <Button size="small" onClick={() => setReplay(value => value + 1)}>Replay preview</Button>}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Checkerboard areas will be transparent in the saved GIF.
      </Typography>
      {current?.error ? <Typography role="alert" color="error">{current.error}</Typography> : (
        <Box sx={{ mt: 1, minHeight: 120, p: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', border: '1px solid #cdd8c6', borderRadius: 1,
          backgroundColor: '#fff',
          backgroundImage: 'conic-gradient(#e1e6de 25%, #fff 0 50%, #e1e6de 0 75%, #fff 0)',
          backgroundSize: '16px 16px' }}>
          {current?.url ? (
            <Box component="img" key={`${current.url}-${replay}`} src={current.url}
              alt="Preview of exported GIF" sx={{ display: 'block', maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
          ) : (
            <Box role="status" sx={{ p: 1, backgroundColor: '#fff' }}>
              <Typography variant="body2">Preparing preview…</Typography>
              <LinearProgress aria-label="Preparing GIF preview" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

GifPreview.propTypes = {
  images: PropTypes.array.isRequired,
  timeline: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  backgroundColor: PropTypes.string,
  transparentColor: PropTypes.string,
  transparencyTolerance: PropTypes.number,
  keepPreviousFrames: PropTypes.bool,
  repeat: PropTypes.number,
};

export default GifPreview;
